(function() {
  'use strict';
  class ProductBundler {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      const configEl = container.querySelector('[data-bundler-config]');
      if (!configEl) {
        console.error('ProductBundler: Config element not found');
        return;
      }
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('ProductBundler: Failed to parse config', e);
        return;
      }
      this.currentStep = 1;
      this.selectedFlavors = new Set();
      this.customQuantities = {};
      this.isAddingToCart = false;
      this.config.variants.forEach(v => { this.customQuantities[v.id] = 0; });
      try {
        this.cacheElements();
        this.bindEvents();
        this.init();
      } catch (e) {
        console.error('ProductBundler: Initialization error', e);
      }
    }
    cacheElements() {
      this.stepLinks = this.container.querySelectorAll('[data-step-link]');
      this.stepContents = this.container.querySelectorAll('[data-step]');
      this.presetCards = this.container.querySelectorAll('[data-select-preset]');
      this.flavorCheckboxes = this.container.querySelectorAll('.bundler__flavor-checkbox');
      this.hintText = this.container.querySelector('[data-hint]');
      this.continueButton = this.container.querySelector('[data-continue-step2]');
      this.errorMessage = this.container.querySelector('[data-error-message]');
      
      // TWO independent toggle positions:
      //  - MAIN (top of step 2): controls only which tier cards are shown
      //  - CUSTOM (inside the bundle builder): controls only the builder mode
      // Flipping one never changes the other. The custom position exists as
      // two DOM copies (one per builder) that always mirror each other.
      this.purchaseToggleBtns = this.container.querySelectorAll('[data-toggle-mode]');
      this.mainToggleBtns = Array.from(this.purchaseToggleBtns).filter(b => !b.closest('.bundler__custom-toggle-wrap'));
      this.customToggleBtns = Array.from(this.purchaseToggleBtns).filter(b => !!b.closest('.bundler__custom-toggle-wrap'));
      this.subscribeContent = this.container.querySelector('[data-subscribe-content]');
      this.onetimeContent = this.container.querySelector('[data-onetime-content]');
      this.subscribeCustom = this.container.querySelector('[data-subscribe-custom]');
      this.onetimeCustom = this.container.querySelector('[data-onetime-custom]');
      this.subscribeBadges = this.container.querySelectorAll('[data-subscribe-badge]');
      const defaultMode = this.config.showSubscriptionOptions ? 'subscribe' : 'onetime';
      this.tierMode = defaultMode;
      this.customMode = defaultMode;
      
      // Toggle benefits panels, grouped by position
      this.toggleBenefitsPanels = this.container.querySelectorAll('[data-toggle-benefits]');
      this.mainBenefitsPanels = Array.from(this.toggleBenefitsPanels).filter(p => !p.closest('.bundler__custom-toggle-wrap'));
      this.customBenefitsPanels = Array.from(this.toggleBenefitsPanels).filter(p => !!p.closest('.bundler__custom-toggle-wrap'));
      this.toggleBenefitItems = this.container.querySelectorAll('[data-toggle-benefit]');
      this.toggleBenefitIcons = this.container.querySelectorAll('[data-benefit-icon]');
      this.toggleBenefitsFooters = this.container.querySelectorAll('[data-toggle-benefits-footer]');
      this.mainToggledOnce = false;
      this.customToggledOnce = false;
      this.onetimeCalloutInitialized = false;
      
      // One-time tier elements
      this.tierClickables = this.container.querySelectorAll('[data-onetime-content] .bundler__tier-card');
      this.tierContainers = this.container.querySelectorAll('[data-onetime-content] .bundler__tier');
      this.customFlavors = this.container.querySelectorAll('[data-onetime-custom] .bundler__custom-flavor');
      this.progressMessage = this.container.querySelector('[data-progress-message]');
      this.progressFills = [
        this.container.querySelector('[data-progress-fill-1]'),
        this.container.querySelector('[data-progress-fill-2]'),
        this.container.querySelector('[data-progress-fill-3]'),
        this.container.querySelector('[data-progress-fill-4]')
      ];
      this.progressSegments = [
        this.container.querySelector('[data-progress-segment="1"]'),
        this.container.querySelector('[data-progress-segment="2"]'),
        this.container.querySelector('[data-progress-segment="3"]'),
        this.container.querySelector('[data-progress-segment="4"]')
      ];
      this.progressCheckmarks = [
        this.container.querySelector('[data-progress-checkmark-1]'),
        this.container.querySelector('[data-progress-checkmark-2]'),
        this.container.querySelector('[data-progress-checkmark-3]'),
        this.container.querySelector('[data-progress-checkmark-4]')
      ];
      this.customAddButton = this.container.querySelector('[data-add-custom]');
      this.customTotalOriginal = this.container.querySelector('[data-custom-total-original]');
      this.customTotalDiscounted = this.container.querySelector('[data-custom-total-discounted]');
      this.customTotalRow = this.container.querySelector('[data-custom-total]');
      this.customTotalSavings = this.container.querySelector('[data-custom-total-savings]');
      
      // One-time custom builder notices + CTA
      this.customNotices = this.container.querySelector('[data-custom-notices]');
      this.noticeShipping = this.container.querySelector('[data-notice-shipping]');
      this.noticeDiscount = this.container.querySelector('[data-notice-discount]');
      this.customCtaWrapper = this.container.querySelector('[data-custom-cta-wrapper]');
      this.customCta = this.container.querySelector('[data-custom-cta]');
      
      // Subscription tier elements
      this.subTierContainers = this.container.querySelectorAll('[data-subscribe-content] .bundler__tier');
      this.subTierButtons = this.container.querySelectorAll('[data-sub-tier-button]');
      this.subCustomFlavors = this.container.querySelectorAll('[data-subscribe-custom] .bundler__custom-flavor');
      this.subProgressBars = this.container.querySelector('[data-sub-progress-bars]');
      this.subProgressMessage = this.container.querySelector('[data-sub-progress-message]');
      this.subProgressFills = [
        this.container.querySelector('[data-sub-progress-fill-1]'),
        this.container.querySelector('[data-sub-progress-fill-2]'),
        this.container.querySelector('[data-sub-progress-fill-3]'),
        this.container.querySelector('[data-sub-progress-fill-4]')
      ];
      this.subProgressSegments = [
        this.container.querySelector('[data-sub-progress-segment="1"]'),
        this.container.querySelector('[data-sub-progress-segment="2"]'),
        this.container.querySelector('[data-sub-progress-segment="3"]'),
        this.container.querySelector('[data-sub-progress-segment="4"]')
      ];
      this.subProgressCheckmarks = [
        this.container.querySelector('[data-sub-progress-checkmark-1]'),
        this.container.querySelector('[data-sub-progress-checkmark-2]'),
        this.container.querySelector('[data-sub-progress-checkmark-3]'),
        this.container.querySelector('[data-sub-progress-checkmark-4]')
      ];
      
      // Subscription custom builder notices + CTA
      this.subCustomNotices = this.container.querySelector('[data-sub-custom-notices]');
      this.subNoticeShipping = this.container.querySelector('[data-sub-notice-shipping]');
      this.subNoticeDiscount = this.container.querySelector('[data-sub-notice-discount]');
      this.subCustomCtaWrapper = this.container.querySelector('[data-sub-custom-cta-wrapper]');
      this.subCustomCta = this.container.querySelector('[data-sub-custom-cta]');
      this.subCustomDisclaimer = this.container.querySelector('[data-sub-custom-disclaimer]');
      
      // Total-price status eyebrows + bag-count lines
      this.customEyebrow = this.container.querySelector('[data-custom-eyebrow]');
      this.eyebrowDiscount = this.container.querySelector('[data-eyebrow-discount]');
      this.eyebrowDiscountText = this.container.querySelector('[data-eyebrow-discount-text]');
      this.eyebrowShipping = this.container.querySelector('[data-eyebrow-shipping]');
      this.customBags = this.container.querySelector('[data-custom-bags]');
      this.subCustomEyebrow = this.container.querySelector('[data-sub-custom-eyebrow]');
      this.subEyebrowDiscount = this.container.querySelector('[data-sub-eyebrow-discount]');
      this.subEyebrowDiscountText = this.container.querySelector('[data-sub-eyebrow-discount-text]');
      this.subEyebrowShipping = this.container.querySelector('[data-sub-eyebrow-shipping]');
      this.subCustomBags = this.container.querySelector('[data-sub-custom-bags]');
      this.subCustomAddButton = this.container.querySelector('[data-sub-add-custom]');
      this.subCustomTotalOriginal = this.container.querySelector('[data-sub-custom-total-original]');
      this.subCustomTotalDiscounted = this.container.querySelector('[data-sub-custom-total-discounted]');
      this.subCustomTotalRow = this.container.querySelector('[data-sub-custom-total]');
      this.subCustomTotalSavings = this.container.querySelector('[data-sub-custom-total-savings]');
      this.subCustomFrequency = this.container.querySelector('[data-sub-custom-frequency]');
      this.subFirstTierCalloutWrapper = this.container.querySelector('[data-sub-first-tier-callout-wrapper]');
      
      // Subscription custom quantities (separate from one-time)
      this.subCustomQuantities = {};
      this.config.variants.forEach(v => { this.subCustomQuantities[v.id] = 0; });
      
      this.stickyButton = this.container.querySelector('[data-sticky-button]');
      this.stickyAction = this.container.querySelector('[data-sticky-action]');
      this.stickyText = this.container.querySelector('[data-sticky-text]');
      this.focusOverlay = document.querySelector('[data-bundler-overlay]');
      this.firstTierCalloutWrapper = this.container.querySelector('[data-first-tier-callout-wrapper]');
      
      // BOGO callout elements
      this.calloutGet = this.container.querySelector('[data-callout-get]');
      this.subCalloutGet = this.container.querySelector('[data-sub-callout-get]');
      this.maxDiscountBenefits = this.container.querySelectorAll('[data-max-discount-benefit]');
    }
    bindEvents() {
      this.stepLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const step = parseInt(link.dataset.stepLink);
          if (step === 1 || (step === 2 && this.selectedFlavors.size > 0)) this.goToStep(step);
        });
      });
      this.presetCards.forEach(card => {
        card.addEventListener('click', (e) => {
          const preset = card.closest('.bundler__preset');
          const button = card.querySelector('.bundler__preset-button');
          this.selectPreset(preset.dataset.variants, button || card);
        });
      });
      this.flavorCheckboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => this.updateFlavorSelection());
      });
      if (this.continueButton) {
        this.continueButton.addEventListener('click', (e) => {
          if (this.selectedFlavors.size > 0) {
            this.hideErrorMessage();
            this.goToStep(2);
          } else {
            this.showErrorMessage();
          }
        });
        this.continueButton.addEventListener('mouseenter', () => {
          if (this.selectedFlavors.size === 0) {
            this.showErrorMessage();
          }
        });
        this.continueButton.addEventListener('mouseleave', () => {
          this.hideErrorMessage();
        });
      }
      // Purchase mode toggle
      this.purchaseToggleBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const mode = btn.dataset.toggleMode;
          if (btn.closest('.bundler__custom-toggle-wrap')) {
            this.setCustomMode(mode, true, { anchor: true });
          } else {
            this.setTierMode(mode, true);
          }
        });
      });
      
      // One-time tier card clicks
      this.tierClickables.forEach(tierCard => {
        tierCard.addEventListener('click', (e) => {
          const tierContainer = tierCard.closest('.bundler__tier');
          const actualQty = parseInt(tierContainer.dataset.actualQuantity);
          const button = tierCard.querySelector('.bundler__tier-button');
          this.addTierToCart(actualQty, button, false);
        });
      });
      
      // Subscription tier card clicks (direct to checkout)
      this.subTierButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const tierContainer = btn.closest('.bundler__tier');
          const actualQty = parseInt(tierContainer.dataset.actualQuantity);
          this.addSubTierToCart(actualQty, btn);
        });
      });
      this.subTierContainers.forEach(tierContainer => {
        const tierCard = tierContainer.querySelector('.bundler__tier-card');
        if (tierCard) {
          tierCard.addEventListener('click', (e) => {
            if (e.target.closest('.bundler__tier-button')) return;
            const actualQty = parseInt(tierContainer.dataset.actualQuantity);
            const button = tierContainer.querySelector('[data-sub-tier-button]');
            this.addSubTierToCart(actualQty, button);
          });
        }
      });
      
      // On mobile the plain `click` on these +/- buttons is unreliable — a tap can
      // land while the fixed sticky bar / mobile URL bar repositions the viewport,
      // and the browser then drops the synthesized click, so it takes more than one
      // tap to change the number. Fire on pointerup (the real release) for touch, and
      // swallow the single ghost click that same tap generates so one tap = one step.
      // Mouse/keyboard still go through click; rapid tapping stays 1 step per tap.
      const bindQtyTap = (btn, handler) => {
        if (!btn) return;
        let swallowClick = false;
        btn.addEventListener('pointerup', (e) => {
          if (e.pointerType === 'mouse') return; // mouse handled by native click below
          swallowClick = true;                   // suppress the click this tap will synthesize
          handler();
        });
        btn.addEventListener('click', () => {
          if (swallowClick) { swallowClick = false; return; }
          handler();
        });
      };

      // One-time custom quantity controls
      this.customFlavors.forEach(flavorEl => {
        const variantId = parseInt(flavorEl.dataset.variantId);
        bindQtyTap(flavorEl.querySelector('[data-qty-minus]'), () => this.updateCustomQuantity(variantId, -1));
        bindQtyTap(flavorEl.querySelector('[data-qty-plus]'), () => this.updateCustomQuantity(variantId, 1));
      });

      // Subscription custom quantity controls
      this.subCustomFlavors.forEach(flavorEl => {
        const variantId = parseInt(flavorEl.dataset.subVariantId);
        bindQtyTap(flavorEl.querySelector('[data-sub-qty-minus]'), () => this.updateSubCustomQuantity(variantId, -1));
        bindQtyTap(flavorEl.querySelector('[data-sub-qty-plus]'), () => this.updateSubCustomQuantity(variantId, 1));
      });
      
      if (this.customAddButton) {
        this.customAddButton.addEventListener('click', () => this.addCustomToCart(false));
      }
      if (this.subCustomAddButton) {
        this.subCustomAddButton.addEventListener('click', () => this.addSubCustomToCart());
      }
      if (this.stickyAction) {
        // Activate on pointerup (the actual touch release), not just click. This
        // sticky bar is position:fixed and repositions on visualViewport changes
        // (mobile URL bar show/hide); a real tap can trigger that reposition and
        // the browser then drops the synthesized click, so the first tap appears
        // to do nothing and the shopper has to tap twice. pointerup fires reliably
        // on release regardless. scrollToSection() is idempotent, so the extra
        // click listener (kept for keyboard/Enter) causing a second call is harmless,
        // but we still de-dupe rapid double-fires.
        let _lastFire = 0;
        const fireScroll = () => {
          var now = (window.performance && performance.now) ? performance.now() : Date.now();
          if (now - _lastFire < 500) return;
          _lastFire = now;
          this.scrollToSection();
        };
        let _px = 0, _py = 0, _moved = false;
        this.stickyAction.addEventListener('pointerdown', (e) => { _px = e.clientX; _py = e.clientY; _moved = false; }, { passive: true });
        this.stickyAction.addEventListener('pointermove', (e) => { if (Math.abs(e.clientX - _px) > 10 || Math.abs(e.clientY - _py) > 10) _moved = true; }, { passive: true });
        this.stickyAction.addEventListener('pointerup', () => { if (!_moved) fireScroll(); });
        this.stickyAction.addEventListener('click', () => fireScroll());
      }
      this.setupStickyObserver();
      this.setupFocusObserver();
      window.addEventListener('pageshow', (e) => { 
        // Reset loading states on any page show (back/forward navigation)
        this.isAddingToCart = false;
        this.clearAllLoadingStates();
        
        // Sync selectedFlavors from actual checkbox states (browser may have preserved them)
        this.syncSelectedFlavorsFromCheckboxes();
        this.updateContinueButton();
        
        // Coming back via bfcache: restore the step recorded in history and pin
        // the viewport to the top of the bundler. Reset only when there's no
        // bundler state at all.
        if (e.persisted) {
          // bfcache restore (e.g. browser Back from checkout): the theme's own
          // cart refresh (theme.js) is gated behind `alpine:init`, which does NOT
          // fire on a restored page, so the mini-cart/header keep their stale
          // pre-add state and the cart looks empty even though it has items.
          // Trigger the theme's cart reload directly to re-sync it with reality.
          try {
            if (window.Alpine && Alpine.store && Alpine.store('xMiniCart') &&
                typeof Alpine.store('xMiniCart').reLoad === 'function') {
              Alpine.store('xMiniCart').reLoad();
            }
          } catch (err) {}

          if (history.state && history.state.bundlerStep !== undefined) {
            this.goToStep(history.state.bundlerStep, true, true, true);
          } else {
            this.reset();
          }
        }
      });
      
      // Also handle visibility change for when tab becomes visible again
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.isAddingToCart = false;
          this.clearAllLoadingStates();
          this.syncSelectedFlavorsFromCheckboxes();
          this.updateContinueButton();
        }
      });
      
      // Handle browser back/forward navigation
      window.addEventListener('popstate', (e) => {
        if (e.state && e.state.bundlerStep !== undefined) {
          this.goToStep(e.state.bundlerStep, false, true);
        } else if (this.currentStep === 2) {
          // No state means we're going back from step 2 to step 1
          this.goToStep(1, false, true);
        }
      });

      this.bindSwipeBack();
    }
    // ----- Independent purchase-mode toggles -----
    applyBenefitsState(panels, isOnetime, firstTime) {
      const apply = () => {
        panels.forEach(panel => {
          panel.querySelectorAll('[data-toggle-benefit]').forEach(item => item.classList.toggle('is-inactive', isOnetime));
          panel.querySelectorAll('[data-benefit-icon]').forEach(icon => icon.classList.toggle('is-x', isOnetime));
          panel.querySelectorAll('[data-toggle-benefits-footer]').forEach(footer => footer.classList.toggle('is-inactive', isOnetime));
        });
      };
      if (firstTime && isOnetime) {
        // Let the expand animation finish before flipping checkmarks to Xs
        setTimeout(apply, 500);
      } else {
        apply();
      }
    }
    setToggleButtonsActive(buttons, mode) {
      buttons.forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.toggleMode === mode);
      });
    }
    // MAIN toggle (top of step 2): affects ONLY the tier-card content
    setTierMode(mode, animate = true) {
      this.tierMode = mode;
      this.setToggleButtonsActive(this.mainToggleBtns, mode);
      const isOnetime = mode === 'onetime';
      
      if (animate && this.mainBenefitsPanels.length) {
        // Dropdown expands the first time THIS position switches to one-time
        // and then stays expanded — it never auto-collapses
        if (isOnetime) {
          this.mainBenefitsPanels.forEach(panel => panel.classList.add('is-expanded'));
        }
        const firstTime = !this.mainToggledOnce && isOnetime;
        if (firstTime) this.mainToggledOnce = true;
        if (this.mainToggledOnce) this.applyBenefitsState(this.mainBenefitsPanels, isOnetime, firstTime);
      }
      
      this.swapRegions(mode, this.subscribeContent, this.onetimeContent, animate, {
        revealSelector: '.bundler__tier-header > *, .bundler__tier-card > *, .bundler__first-tier-callout'
      });
      
      if (isOnetime && !this.onetimeCalloutInitialized) {
        this.onetimeCalloutInitialized = true;
        this.initCalloutAnimation();
      }
      this.updateSubscribeBadge();
    }
    // CUSTOM toggle (inside the bundle builder): affects ONLY the builder.
    // Both DOM copies of this toggle mirror each other.
    setCustomMode(mode, animate = true, options = {}) {
      this.customMode = mode;
      this.setToggleButtonsActive(this.customToggleBtns, mode);
      const isOnetime = mode === 'onetime';
      
      if (animate && this.customBenefitsPanels.length) {
        if (isOnetime) {
          this.customBenefitsPanels.forEach(panel => panel.classList.add('is-expanded'));
        }
        const firstTime = !this.customToggledOnce && isOnetime;
        if (firstTime) this.customToggledOnce = true;
        if (this.customToggledOnce) this.applyBenefitsState(this.customBenefitsPanels, isOnetime, firstTime);
      }
      
      this.swapRegions(mode, this.subscribeCustom, this.onetimeCustom, animate, {
        anchor: options.anchor,
        revealSelector: '.bundler__progress > *, .bundler__total-eyebrow, .bundler__custom-total, .bundler__total-bags'
      });
      this.updateSubscribeBadge();
    }
    // Shared region swap: instant layout switch, optional viewport anchoring,
    // skeleton shimmer then content fade-in on the incoming region
    swapRegions(mode, subscribeRegion, onetimeRegion, animate, options = {}) {
      const currentRegion = mode === 'subscribe' ? onetimeRegion : subscribeRegion;
      const newRegion = mode === 'subscribe' ? subscribeRegion : onetimeRegion;
      
      if (animate && currentRegion && newRegion && currentRegion.classList.contains('is-active')) {
        let oldAnchorTop = null;
        if (options.anchor) {
          const anchorOld = currentRegion.querySelector('.bundler__custom-toggle-wrap');
          if (anchorOld) oldAnchorTop = anchorOld.getBoundingClientRect().top;
        }
        
        currentRegion.classList.remove('is-active', 'bundler__mode-skeleton');
        newRegion.classList.add('is-active');
        
        newRegion.classList.add('bundler__mode-skeleton');
        if (newRegion._bundlerSkeletonTimer) clearTimeout(newRegion._bundlerSkeletonTimer);
        newRegion._bundlerSkeletonTimer = setTimeout(() => {
          newRegion.classList.remove('bundler__mode-skeleton');
          if (options.revealSelector) {
            newRegion.querySelectorAll(options.revealSelector).forEach(el => {
              el.classList.remove('bundler__fade-in');
              el.offsetHeight; // restart animation
              el.classList.add('bundler__fade-in');
            });
          }
        }, 400);
        
        // Keep the toggle the user clicked pinned in the viewport
        if (oldAnchorTop !== null) {
          const anchorNew = newRegion.querySelector('.bundler__custom-toggle-wrap');
          if (anchorNew && typeof window.scrollBy === 'function') {
            const delta = anchorNew.getBoundingClientRect().top - oldAnchorTop;
            if (delta !== 0) {
              try {
                window.scrollBy({ top: delta, left: 0, behavior: 'instant' });
              } catch (e) {
                window.scrollBy(0, delta);
              }
            }
          }
        }
      } else {
        if (subscribeRegion) subscribeRegion.classList.toggle('is-active', mode === 'subscribe');
        if (onetimeRegion) onetimeRegion.classList.toggle('is-active', mode === 'onetime');
      }
    }
    updateSubscribeBadge() {
      if (!this.subscribeBadges.length) return;
      const flavorCount = this.selectedFlavors.size;
      const tierQuantities = flavorCount >= 3 ? [9, 6, 3] : [8, 6, 4];
      const maxQty = tierQuantities[0];
      const maxDiscount = this.getSubDiscount(maxQty) || 60;
      this.subscribeBadges.forEach(badge => {
        badge.textContent = `SAVE ${maxDiscount}%`;
      });
    }
    init() {
      // Select all available flavors by default on fresh page load
      this.selectAllFlavorsIfFreshLoad();
      
      this.updateHintVisibility();
      this.updateContinueButton();
      this.updateProgressBar();
      this.updateSubProgressBar();
      this.initSubCalloutAnimation();
      // setTierMode only arms the one-time callout on a switch INTO one-time.
      // With subscriptions turned off one-time is the only mode, that switch
      // never happens, and the callout would stay collapsed forever.
      if (this.tierMode === 'onetime' && !this.onetimeCalloutInitialized) {
        this.onetimeCalloutInitialized = true;
        this.initCalloutAnimation();
      }
      
      // Set initial history state for step 1 (so back button works from step 2)
      if (!history.state || history.state.bundlerStep === undefined) {
        history.replaceState({ bundlerStep: 1 }, '', window.location.href);
      } else if (history.state.bundlerStep === 2) {
        // Returning to the page (e.g. back from checkout after a full page load):
        // the history entry still says step 2, but the page renders step 1 by
        // default while the browser restores the taller step-2 scroll offset —
        // landing the visitor far down the page. Restore step 2 ourselves and
        // pin the viewport to the top of the bundler instead. Pressing back
        // again pops to the step-1 entry as usual.
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'manual';
        }
        this.goToStep(2, true, true, true);
      }
    }
    selectAllFlavorsIfFreshLoad() {
      // Only select all if no checkboxes are currently checked (fresh load)
      const anyChecked = Array.from(this.flavorCheckboxes).some(cb => cb.checked);
      if (anyChecked) return; // User already has a selection, don't override
      
      // Check all available flavor checkboxes
      this.flavorCheckboxes.forEach(checkbox => {
        if (!checkbox.disabled) {
          checkbox.checked = true;
          this.selectedFlavors.add(parseInt(checkbox.value));
        }
      });
    }
    initCalloutAnimation() {
      if (!this.firstTierCalloutWrapper || !this.tierContainers.length) return;
      
      const firstTierCard = this.tierContainers[0];
      if (!firstTierCard) return;
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.95) {
            this.firstTierCalloutWrapper.classList.add('is-animated');
            observer.unobserve(firstTierCard);
          }
        });
      }, { threshold: 0.95 });
      
      observer.observe(firstTierCard);
    }
    initSubCalloutAnimation() {
      if (!this.subFirstTierCalloutWrapper || !this.subTierContainers.length) return;
      
      const firstTierCard = this.subTierContainers[0];
      if (!firstTierCard) return;
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.95) {
            this.subFirstTierCalloutWrapper.classList.add('is-animated');
            observer.unobserve(firstTierCard);
          }
        });
      }, { threshold: 0.95 });
      
      observer.observe(firstTierCard);
    }
    reset() {
      this.currentStep = 1;
      this.selectedFlavors.clear();
      this.config.variants.forEach(v => { 
        this.customQuantities[v.id] = 0;
        this.subCustomQuantities[v.id] = 0;
      });
      this.flavorCheckboxes.forEach(cb => { cb.checked = false; });
      this.clearAllLoadingStates();
      
      // Reset toggle benefits panels
      this.mainToggledOnce = false;
      this.customToggledOnce = false;
      this.onetimeCalloutInitialized = false;
      this.toggleBenefitsPanels.forEach(panel => {
        panel.classList.remove('is-expanded');
      });
      this.toggleBenefitItems.forEach(item => {
        item.classList.remove('is-inactive');
      });
      this.toggleBenefitIcons.forEach(icon => {
        icon.classList.remove('is-x');
      });
      this.toggleBenefitsFooters.forEach(footer => {
        footer.classList.remove('is-inactive');
      });
      
      this.goToStep(1, true);
      this.updateHintVisibility();
      this.updateContinueButton();
      this.updateCustomQuantityDisplays();
      this.updateSubCustomQuantityDisplays();
      this.updateProgressBar();
      this.updateSubProgressBar();
      const resetMode = this.config.showSubscriptionOptions ? 'subscribe' : 'onetime';
      this.setTierMode(resetMode, false);
      this.setCustomMode(resetMode, false);
    }
    clearAllLoadingStates() {
      this.container.querySelectorAll('.is-loading').forEach(el => {
        el.classList.remove('is-loading');
      });
    }
    // Mobile: a left-to-right swipe anywhere on step 2 goes back to step 1, the
    // way an OS back gesture reads. Only a deliberate, mostly-horizontal drag
    // counts — once the finger has drifted far enough vertically we call it a
    // page scroll and stop watching. Swipes that start hard against the left edge
    // are left alone, because the browser's own back gesture owns that strip.
    bindSwipeBack() {
      const stepTwo = this.container.querySelector('[data-step="2"]');
      if (!stepTwo) return;
      if (window.matchMedia && !window.matchMedia('(pointer: coarse)').matches) return;

      const EDGE_GUARD = 24; // px of left edge the browser gesture owns
      const MIN_X = 70;      // px the finger must travel right to count
      const MAX_Y = 50;      // px of vertical drift before it's a scroll
      let startX = 0, startY = 0, tracking = false;

      stepTwo.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) { tracking = false; return; }
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = startX > EDGE_GUARD;
      }, { passive: true });

      stepTwo.addEventListener('touchmove', (e) => {
        if (!tracking) return;
        if (e.touches.length !== 1) { tracking = false; return; }
        if (Math.abs(e.touches[0].clientY - startY) > MAX_Y) tracking = false;
      }, { passive: true });

      stepTwo.addEventListener('touchcancel', () => { tracking = false; }, { passive: true });

      stepTwo.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        if (this.currentStep !== 2) return;
        const t = e.changedTouches[0];
        if (!t) return;
        if (t.clientX - startX < MIN_X) return;
        if (Math.abs(t.clientY - startY) > MAX_Y) return;
        // Pin the top of the bundler in view BEFORE the panels swap. The crumb
        // sits at the top, so tapping it means you are already there and the
        // slide plays in view; a swipe fires wherever you are, usually far down
        // a step 2 that is much taller than step 1. Without this the panels swap,
        // the container collapses to step-1 height, and the whole slide plays off
        // the top of the screen while a smooth scroll crawls up to meet it.
        this.scrollToSection();
        // Same route as tapping the step-1 crumb, so the two stay in sync; the
        // instant flag stops goToStep's trailing smooth scroll re-running it.
        this.goToStep(1, false, false, true);
      }, { passive: true });
    }

    goToStep(step, skipAnimation = false, fromPopState = false, instantScroll = false) {
      const previousStep = this.currentStep;
      this.currentStep = step;
      
      // Clear any loading states when changing steps
      this.clearAllLoadingStates();
      
      // Push history state when going to step 2 (not when triggered by popstate)
      if (!fromPopState && step === 2 && previousStep === 1) {
        history.pushState({ bundlerStep: 2 }, '', window.location.href);
      }
      
      this.stepLinks.forEach(link => {
        const linkStep = parseInt(link.dataset.stepLink);
        link.classList.toggle('is-active', linkStep === step);
        link.classList.toggle('is-completed', linkStep < step);
      });
      
      if (skipAnimation) {
        this.stepContents.forEach(content => {
          content.classList.remove('is-sliding-out-left', 'is-sliding-out-right', 'is-sliding-in-left', 'is-sliding-in-right');
          content.classList.toggle('is-active', parseInt(content.dataset.step) === step);
        });
      } else {
        const currentContent = this.container.querySelector(`[data-step="${previousStep}"]`);
        const nextContent = this.container.querySelector(`[data-step="${step}"]`);
        
        if (currentContent && nextContent && previousStep !== step) {
          const goingForward = step > previousStep;

          // Park the incoming panel off-screen on the side it should enter from,
          // with the transition switched OFF. This matters: the base rule gives
          // every inactive panel BOTH `transform: translateX(100%)` and a live
          // `transition: transform .3s`, so an idle step 1 sits off to the RIGHT
          // and merely adding `is-sliding-in-left` animates it leftward instead of
          // teleporting it. A forced reflow flushes layout but does not cancel a
          // running transition, so a frame later we would ask it to travel to 0
          // from a position still hard against the right edge — a back transition
          // that slides in from the wrong side. Killing the transition first makes
          // the start position instant, so only the real move is animated.
          nextContent.classList.remove('is-active');
          nextContent.style.transition = 'none';
          nextContent.classList.add(goingForward ? 'is-sliding-in-right' : 'is-sliding-in-left');
          nextContent.offsetHeight;          // commit the off-screen start position

          // Hand the transition back to the stylesheet and commit that too, so the
          // move to 0 below is the first thing the restored transition sees.
          nextContent.style.transition = '';
          nextContent.offsetHeight;

          // Animate out current, animate in next
          currentContent.classList.add(goingForward ? 'is-sliding-out-left' : 'is-sliding-out-right');
          currentContent.classList.remove('is-active');
          nextContent.classList.remove('is-sliding-in-right', 'is-sliding-in-left');
          nextContent.classList.add('is-active');

          // Clean up once the slide is done. Same trick: drop the transition while
          // the outgoing panel snaps back to its parked state, or it spends another
          // 300ms invisibly travelling across the section.
          setTimeout(() => {
            currentContent.style.transition = 'none';
            currentContent.classList.remove('is-sliding-out-left', 'is-sliding-out-right');
            currentContent.offsetHeight;
            currentContent.style.transition = '';
          }, 300);
        }
      }
      
      // Update button state when going back to step 1
      if (step === 1) {
        this.updateContinueButton();
        this.updateHintVisibility();
      }
      
      this.updateStickyText();
      if (step === 2) {
        this.updateTierDisplays();
        this.updateSubTierDisplays();
        this.initializeCustomQuantities();
        this.initializeSubCustomQuantities();
        this.updateProgressBar();
        this.updateSubProgressBar();
        this.updateSubscribeBadge();
      }
      this.container.scrollIntoView({ behavior: instantScroll ? 'auto' : 'smooth', block: 'start' });
    }
    selectPreset(variantsString, buttonElement) {
      const variantNames = variantsString.split(',').map(s => s.trim());
      this.selectedFlavors.clear();
      this.flavorCheckboxes.forEach(cb => cb.checked = false);
      variantNames.forEach(name => {
        const variant = this.config.variants.find(v => v.title.toLowerCase() === name.toLowerCase());
        if (variant) {
          this.selectedFlavors.add(variant.id);
          const checkbox = this.container.querySelector(`.bundler__flavor-checkbox[value="${variant.id}"]`);
          if (checkbox) checkbox.checked = true;
        }
      });
      this.updateHintVisibility();
      this.updateContinueButton();
      setTimeout(() => this.goToStep(2), 150);
    }
    syncSelectedFlavorsFromCheckboxes() {
      // Sync the selectedFlavors Set from the actual checkbox states
      // This is needed when browser restores checkbox states on back navigation
      this.selectedFlavors.clear();
      this.flavorCheckboxes.forEach(checkbox => {
        if (checkbox.checked) this.selectedFlavors.add(parseInt(checkbox.value));
      });
    }
    updateFlavorSelection() {
      this.selectedFlavors.clear();
      this.flavorCheckboxes.forEach(checkbox => {
        if (checkbox.checked) this.selectedFlavors.add(parseInt(checkbox.value));
      });
      this.updateHintVisibility();
      this.updateContinueButton();
      if (this.selectedFlavors.size > 0) {
        this.hideErrorMessage();
      }
    }
    updateHintVisibility() {
      if (this.hintText) this.hintText.classList.toggle('is-visible', this.selectedFlavors.size === 1);
    }
    updateContinueButton() {
      if (this.continueButton) this.continueButton.classList.toggle('is-disabled', this.selectedFlavors.size === 0);
    }
    showErrorMessage() {
      if (this.errorMessage) {
        this.errorMessage.classList.add('is-visible');
        // Auto-hide after 5 seconds
        if (this.errorTimeout) clearTimeout(this.errorTimeout);
        this.errorTimeout = setTimeout(() => {
          this.hideErrorMessage();
        }, 5000);
      }
    }
    hideErrorMessage() {
      if (this.errorMessage) {
        this.errorMessage.classList.remove('is-visible');
        if (this.errorTimeout) {
          clearTimeout(this.errorTimeout);
          this.errorTimeout = null;
        }
      }
    }
    updateTierDisplays() {
      const flavorCount = this.selectedFlavors.size;
      const selectedVariants = this.config.variants.filter(v => this.selectedFlavors.has(v.id));
      const basePrice = this.config.variants[0]?.price || 0;
      
      // Determine which tier quantities to show based on flavor count
      // 3+ flavors: 9, 6, 3 (divisible by 3)
      // 1-2 flavors: 8, 6, 4 (minimum 3 bags)
      const tierQuantities = flavorCount >= 3 ? [9, 6, 3] : [8, 6, 4];
      
      // Build icon HTML for bullets
      const checkmarkIconHtml = this.config.checkmarkIconUrl 
        ? `<span class="bundler__bullet-checkmark"><img src="${this.config.checkmarkIconUrl}" alt="" loading="lazy" width="16" height="16"></span>`
        : '<span class="bundler__bullet-checkmark"><span class="bundler__bullet-checkmark-fallback">✓</span></span>';
      const noShippingIconHtml = this.config.noShippingIconUrl 
        ? `<span class="bundler__bullet-checkmark"><img src="${this.config.noShippingIconUrl}" alt="" loading="lazy" width="16" height="16"></span>`
        : '<span class="bundler__bullet-checkmark"><span class="bundler__bullet-checkmark-fallback">✗</span></span>';
      
      this.tierContainers.forEach((tierContainer, index) => {
        const actualQty = tierQuantities[index];
        if (!actualQty) return;
        
        // Update the data attribute for cart logic
        tierContainer.dataset.actualQuantity = actualQty;
        
        const distribution = this.getDistribution(flavorCount, actualQty);
        const discountPercent = this.getOnetimeDiscount(actualQty);
        const pricePerBag = this.getPriceForQuantity(actualQty);
        
        // Update quantity display
        const qtyDisplay = tierContainer.querySelector('[data-tier-qty-display]');
        if (qtyDisplay) qtyDisplay.textContent = `${actualQty} BAGS`;
        
        // Update price display
        const priceDisplay = tierContainer.querySelector('[data-tier-price-display]');
        if (priceDisplay) priceDisplay.textContent = this.formatMoney(pricePerBag);
        
        // Update pint price
        const pintDisplay = tierContainer.querySelector('[data-tier-pint-display]');
        if (pintDisplay) {
          const pintIconHtml = this.config.pintIconUrl ? `<img src="${this.config.pintIconUrl}" alt="" class="bundler__pint-icon" width="16" height="16">` : '';
          pintDisplay.innerHTML = `only <span class="bundler__tier-pint-price-value">${this.formatMoneyWithCents(pricePerBag / this.config.pintsPerBag)}</span> per pint ${pintIconHtml}`;
        }
        
        // Update savings display
        const savingsDisplay = tierContainer.querySelector('[data-tier-savings]');
        if (savingsDisplay) {
          const totalSavings = (basePrice - pricePerBag) * actualQty;
          savingsDisplay.innerHTML = `<span class="bundler__tier-savings-label">Save</span> <span class="bundler__tier-savings-amount">${this.formatMoney(totalSavings)}</span> <span class="bundler__tier-savings-suffix">for limited time!</span>`;
        }
        
        // Update flavor images
        const flavorsContainer = tierContainer.querySelector('[data-tier-flavors]');
        if (flavorsContainer) {
          flavorsContainer.innerHTML = selectedVariants.map(v => `<div class="bundler__tier-flavor-img"><img src="${v.image}" alt="${v.title}" loading="lazy" width="50" height="50"></div>`).join('');
        }
        
        // Update distribution text
        const distributionEl = tierContainer.querySelector('[data-tier-distribution]');
        if (distributionEl) distributionEl.textContent = this.formatDistribution(selectedVariants, distribution);
        
        // Update header discount text
        const headerDiscountEl = tierContainer.querySelector('[data-tier-header-discount]');
        if (headerDiscountEl) {
          headerDiscountEl.textContent = `SAVE ${discountPercent}%`;
        }
        
        // Update the first bullet point with discount
        const discountText = tierContainer.querySelector('[data-tier-discount-text]');
        if (discountText) {
          discountText.textContent = `${discountPercent}% OFF`;
        }
        
        // Update shipping bullet based on quantity (NO FREE SHIPPING for 3 or fewer bags)
        const shippingIconEl = tierContainer.querySelector('[data-tier-shipping-icon]');
        const shippingTextEl = tierContainer.querySelector('[data-tier-shipping-text]');
        if (shippingIconEl && shippingTextEl) {
          if (actualQty <= 3) {
            shippingIconEl.innerHTML = noShippingIconHtml;
            shippingTextEl.textContent = 'NO FREE SHIPPING';
          } else {
            shippingIconEl.innerHTML = checkmarkIconHtml;
            shippingTextEl.textContent = this.config.shippingBulletText || 'FREE SHIPPING';
          }
        }
        
        // Update total price display (same rounding rule as everywhere else)
        const totalOriginalEl = tierContainer.querySelector('[data-tier-total-original]');
        const totalDiscountedEl = tierContainer.querySelector('[data-tier-total-discounted]');
        if (totalOriginalEl && totalDiscountedEl) {
          totalOriginalEl.textContent = this.formatMoney(basePrice * actualQty);
          totalDiscountedEl.textContent = this.formatMoney(pricePerBag * actualQty);
        }
      });
      
      // Update first tier callout with BOGO text
      if (this.calloutGet && this.tierContainers.length > 0) {
        const firstTierQty = tierQuantities[0];
        const firstTierDiscount = this.getOnetimeDiscount(firstTierQty);
        this.calloutGet.textContent = this.getBogoText(firstTierQty, firstTierDiscount);
      }
    }
    updateSubTierDisplays() {
      const flavorCount = this.selectedFlavors.size;
      const selectedVariants = this.config.variants.filter(v => this.selectedFlavors.has(v.id));
      const basePrice = this.config.variants[0]?.price || 0;
      
      // Subscription tier quantities: same logic as one-time
      // 3+ flavors: 9, 6, 3
      // 1-2 flavors: 8, 6, 4
      const tierQuantities = flavorCount >= 3 ? [9, 6, 3] : [8, 6, 4];
      
      // Frequency text based on quantity: 3 → 1 month, 4-6 → 6 weeks, 7+ → 2 months
      const getFrequencyText = (qty) => {
        if (qty >= 7) return '2 months';
        if (qty >= 4) return '6 weeks';
        return 'month';
      };
      
      this.subTierContainers.forEach((tierContainer, index) => {
        const actualQty = tierQuantities[index];
        if (!actualQty) return;
        
        // Update the data attribute for cart logic
        tierContainer.dataset.actualQuantity = actualQty;
        
        const distribution = this.getDistribution(flavorCount, actualQty);
        const discountPercent = this.getSubDiscount(actualQty);
        const pricePerBag = this.getSubPriceForQuantity(actualQty);
        
        // Update quantity display
        const qtyDisplay = tierContainer.querySelector('[data-sub-tier-qty-display]');
        if (qtyDisplay) qtyDisplay.textContent = `${actualQty} BAGS`;
        
        // Update price display
        const priceDisplay = tierContainer.querySelector('[data-sub-tier-price-display]');
        if (priceDisplay) priceDisplay.textContent = this.formatMoney(pricePerBag);
        
        // Update pint price
        const pintDisplay = tierContainer.querySelector('[data-sub-tier-pint-display]');
        if (pintDisplay) {
          const pintIconHtml = this.config.pintIconUrl ? `<img src="${this.config.pintIconUrl}" alt="" class="bundler__pint-icon" width="16" height="16">` : '';
          pintDisplay.innerHTML = `only <span class="bundler__tier-pint-price-value">${this.formatMoneyWithCents(pricePerBag / this.config.pintsPerBag)}</span> per pint ${pintIconHtml}`;
        }
        
        // Update savings display
        const savingsDisplay = tierContainer.querySelector('[data-sub-tier-savings]');
        if (savingsDisplay) {
          const totalSavings = (basePrice - pricePerBag) * actualQty;
          savingsDisplay.innerHTML = `<span class="bundler__tier-savings-label">Save</span> <span class="bundler__tier-savings-amount">${this.formatMoney(totalSavings)}</span> <span class="bundler__tier-savings-suffix">for limited time!</span>`;
        }
        
        // Update flavor images
        const flavorsContainer = tierContainer.querySelector('[data-sub-tier-flavors]');
        if (flavorsContainer) {
          flavorsContainer.innerHTML = selectedVariants.map(v => `<div class="bundler__tier-flavor-img"><img src="${v.image}" alt="${v.title}" loading="lazy" width="50" height="50"></div>`).join('');
        }
        
        // Update distribution text
        const distributionEl = tierContainer.querySelector('[data-sub-tier-distribution]');
        if (distributionEl) distributionEl.textContent = this.formatDistribution(selectedVariants, distribution);
        
        // Update header discount text — preserve the asterisk span when updating
        const headerDiscountEl = tierContainer.querySelector('[data-sub-tier-header-discount]');
        if (headerDiscountEl) {
          headerDiscountEl.innerHTML = `SAVE ${discountPercent}%<span class="bundler__tier-header-asterisk">*</span>`;
        }
        
        // Update the first bullet point with discount
        const discountText = tierContainer.querySelector('[data-sub-tier-discount-text]');
        if (discountText) {
          discountText.textContent = `${discountPercent}% OFF`;
        }
        
        // Update disclaimer renewal discount (intro discount minus 10%, except
        // the 2-bag 15% tier, which keeps its rate on renewal — the admin's
        // "for all recurring payments" discount at min qty 2 is also 15%.
        // Same rule as snippets/fc-cart-autoship.liquid.)
        const disclaimerEl = tierContainer.querySelector('[data-sub-tier-disclaimer]');
        if (disclaimerEl) {
          const renewalDiscount = discountPercent > 15 ? discountPercent - 10 : discountPercent;
          disclaimerEl.textContent = `*Intro offer discount. Subscription will renew at ${renewalDiscount}% off. Final serving volume will vary based on preparation ingredients/method.`;
        }
        
        // Update frequency text
        const frequencyEl = tierContainer.querySelector('[data-sub-tier-frequency]');
        if (frequencyEl) {
          const freqText = getFrequencyText(actualQty);
          frequencyEl.textContent = `Shipped every ${freqText}—pause, change or cancel subscription anytime`;
        }
        
        // Update order total
        const totalOriginalEl = tierContainer.querySelector('[data-sub-tier-total-original]');
        const totalDiscountedEl = tierContainer.querySelector('[data-sub-tier-total-discounted]');
        if (totalOriginalEl) {
          totalOriginalEl.textContent = this.formatMoney(basePrice * actualQty);
        }
        if (totalDiscountedEl) {
          totalDiscountedEl.textContent = this.formatMoney(pricePerBag * actualQty);
        }
      });
      
      // Update subscription first tier callout with BOGO text
      if (this.subCalloutGet && this.subTierContainers.length > 0) {
        const firstTierQty = tierQuantities[0];
        const firstTierDiscount = this.getSubDiscount(firstTierQty);
        this.subCalloutGet.textContent = this.getBogoText(firstTierQty, firstTierDiscount);
        
        // Update the "Up to X% off" benefit text (all toggle instances)
        this.maxDiscountBenefits.forEach(el => {
          el.textContent = `Up to ${firstTierDiscount}% off`;
        });
      }
    }
    // Discounts are threshold-based: the highest threshold <= total applies.
    // (e.g. with an 8+ top tier, 9 or 10 bags still resolve to the 8+ discount)
    getThresholdDiscount(total, map) {
      let discount = 0;
      const keys = Object.keys(map).map(Number).sort((a, b) => b - a);
      for (const qty of keys) {
        if (total >= qty) {
          discount = map[qty];
          break;
        }
      }
      return discount;
    }
    getOnetimeDiscount(total) {
      return this.getThresholdDiscount(total, this.config.discounts);
    }
    getSubDiscount(total) {
      return this.getThresholdDiscount(total, this.config.subscriptionDiscounts);
    }
    // Mirror how Shopify actually charges the automatic percentage discounts:
    // the percentage is applied to the UNIT price and the discount amount is
    // TRUNCATED to the cent per unit, then multiplied by quantity.
    // $39.99 at 40% off → $15.996 discount → $15.99 → $24.00/bag → $216.00 for 9.
    // (Rounding the discount to the nearest cent would give $23.99/bag and
    // $215.91, which is NOT what checkout charges — verified against orders
    // #1003/#1007/#1008 at 9 bags one-time and #1001 at 9 bags subscription.)
    getDiscountedUnitPrice(basePrice, discountPercent) {
      // toFixed(6) first so float noise can't drop the value below a whole cent.
      const unitDiscount = Math.floor(Number((basePrice * discountPercent / 100).toFixed(6)));
      return basePrice - unitDiscount;
    }
    getSubPriceForQuantity(quantity) {
      const basePrice = this.config.variants[0]?.price || 0;
      return this.getDiscountedUnitPrice(basePrice, this.getSubDiscount(quantity));
    }
    getPriceForQuantity(quantity) {
      const basePrice = this.config.variants[0]?.price || 0;
      return this.getDiscountedUnitPrice(basePrice, this.getOnetimeDiscount(quantity));
    }
    getBogoText(quantity, discountPercent) {
      // Generic BOGO math: bags paid for = quantity at the discounted price,
      // rounded to nearest; the rest are free. Reproduces the original
      // hand-picked mappings (9/60 → 5 free, 6/55 → 3 free, 9/40 → 4 free,
      // 6/35 → 2 free) and handles the 8-bag tiers (8/60 → 5, 8/40 → 3).
      const paid = Math.max(1, Math.round(quantity * (100 - discountPercent) / 100));
      const free = quantity - paid;
      if (free > 0) {
        return `Get ${free} bag${free !== 1 ? 's' : ''} free`;
      }
      // Fallback when the discount is too small to give a full free bag
      return `Get ${discountPercent}% off`;
    }
    formatMoney(cents) {
      // Snap to a whole cent first (kills float noise like 16199.999999999996),
      // then drop the decimals only when the amount IS a whole dollar. Prices
      // here are compared against the cart, so we must never truncate them:
      // at $39.99 every tier lands on a whole dollar anyway, and a base price
      // that doesn't will show the real cents instead of a number nobody is
      // charged.
      const whole = Math.round(cents);
      if (whole % 100 === 0) return `$${whole / 100}`;
      return `$${(whole / 100).toFixed(2)}`;
    }
    formatMoneyWithCents(cents) {
      // Keep decimals for pint prices
      const dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return `$${dollars}`;
    }
    getDistribution(flavorCount, totalQuantity) {
      // For equal distribution among flavors
      if (flavorCount > 0) {
        const perFlavor = Math.floor(totalQuantity / flavorCount);
        const remainder = totalQuantity % flavorCount;
        const distribution = Array(flavorCount).fill(perFlavor);
        // Distribute remainder to first flavors
        for (let i = 0; i < remainder; i++) {
          distribution[i]++;
        }
        return distribution;
      }
      return [totalQuantity];
    }
    formatDistribution(variants, distribution) {
      if (variants.length === 1) {
        return `${distribution[0]} bag${distribution[0] !== 1 ? 's' : ''} of ${variants[0].title}`;
      }
      // Check if all quantities are equal
      const allEqual = distribution.every(qty => qty === distribution[0]);
      if (allEqual) {
        return `${distribution[0]} bag${distribution[0] !== 1 ? 's' : ''} of each flavor`;
      }
      // Otherwise list each flavor
      const parts = variants.map((v, i) => `${distribution[i] || 0} ${v.title}`);
      return parts.join(', ');
    }
    formatVariantNames(variants) {
      if (variants.length === 0) return '';
      if (variants.length === 1) return variants[0].title;
      if (variants.length === 2) return `${variants[0].title} & ${variants[1].title}`;
      // 3+ variants: "Vanilla, Chocolate & Strawberry"
      const allButLast = variants.slice(0, -1).map(v => v.title).join(', ');
      const last = variants[variants.length - 1].title;
      return `${allButLast} & ${last}`;
    }
    // Default bags per available flavor, regardless of step-1 selection.
    // Aims for the top discount tier: 3+ flavors -> 3 each (9+), 2 -> 4 + 4, 1 -> 8.
    defaultCustomQtyPerFlavor() {
      const availableCount = this.config.variants.filter(v => v.available).length;
      if (availableCount >= 3) return 3;
      if (availableCount === 2) return 4;
      return 8;
    }
    initializeCustomQuantities() {
      const perFlavor = this.defaultCustomQtyPerFlavor();
      this.config.variants.forEach(v => {
        this.customQuantities[v.id] = v.available ? perFlavor : 0;
      });
      this.updateCustomQuantityDisplays();
      this.updateProgressBar();
    }
    updateCustomQuantity(variantId, delta) {
      this.customQuantities[variantId] = Math.max(0, (this.customQuantities[variantId] || 0) + delta);
      // Keep subscription builder in sync so toggling modes never "loses" selections
      this.subCustomQuantities[variantId] = this.customQuantities[variantId];
      this.updateCustomQuantityDisplays();
      this.updateSubCustomQuantityDisplays();
      this.updateProgressBar();
      this.updateSubProgressBar();
    }
    updateCustomQuantityDisplays() {
      this.customFlavors.forEach(flavorEl => {
        const variantId = parseInt(flavorEl.dataset.variantId);
        const qty = this.customQuantities[variantId] || 0;
        const valueEl = flavorEl.querySelector('[data-qty-value]');
        if (valueEl) valueEl.textContent = qty;
        flavorEl.classList.toggle('is-selected', qty > 0);
      });
    }
    getTotalCustomQuantity() {
      return Object.values(this.customQuantities).reduce((sum, qty) => sum + qty, 0);
    }
    // Shared helper: fill/complete states for a set of quantity thresholds
    applyProgressSegments(total, thresholds, fills, segments, checkmarks) {
      thresholds.forEach((target, i) => {
        const prev = i === 0 ? 0 : thresholds[i - 1];
        const fill = Math.max(0, Math.min(1, (total - prev) / (target - prev))) * 100;
        const complete = total >= target;
        if (fills[i]) fills[i].style.width = `${fill}%`;
        if (segments[i]) {
          // Fire the unlock animation only on the transition into completion,
          // not on every re-render — otherwise it replays on unrelated updates.
          const wasComplete = segments[i].classList.contains('is-complete');
          segments[i].classList.toggle('is-complete', complete);
          if (complete && !wasComplete) {
            const seg = segments[i];
            seg.classList.remove('just-unlocked');
            void seg.offsetWidth; // reflow, so the animation restarts cleanly
            seg.classList.add('just-unlocked');
            setTimeout(() => seg.classList.remove('just-unlocked'), 900);
          }
        }
        if (checkmarks[i]) checkmarks[i].classList.toggle('is-visible', complete);
      });
    }
    updateProgressBar() {
      const total = this.getTotalCustomQuantity();
      
      // One-time discount tiers from config (first discount at 2 bags, free shipping at 4+)
      const discount2 = this.config.discounts[2] || 15;
      const discount3 = this.config.discounts[3] || 20;
      const discount4 = this.config.discounts[4] || 30;
      const discount6 = this.config.discounts[6] || 35;
      const discount8 = this.config.discounts[8] || 40;
      
      let message = '';
      if (total === 0) {
        message = `Add 2 bags for <strong>${discount2}% off!</strong>`;
      } else if (total === 1) {
        message = `Add 1 more bag for <strong>${discount2}% off!</strong>`;
      } else if (total === 2) {
        message = `Add 1 more bag for <strong>${discount3}% off!</strong>`;
      } else if (total === 3) {
        message = `Add 1 more bag for <strong>${discount4}% off + free shipping!</strong>`;
      } else if (total === 4) {
        message = `Add 2 more bags for <strong>${discount6}% off!</strong>`;
      } else if (total === 5) {
        message = `Add 1 more bag for <strong>${discount6}% off!</strong>`;
      } else if (total >= 6 && total < 8) {
        const needed = 8 - total;
        message = `Add ${needed} more bag${needed !== 1 ? 's' : ''} for <strong>${discount8}% off!</strong>`;
      } else if (total >= 8) {
        message = `<strong>${discount8}% off + free shipping</strong> unlocked!`;
      }
      
      if (this.progressMessage) {
        this.progressMessage.innerHTML = message;
      }
      
      // Segments: 20% at 3, 30% + free shipping at 4, 35% at 6, 40% at 8
      this.applyProgressSegments(total, [3, 4, 6, 8], this.progressFills, this.progressSegments, this.progressCheckmarks);
      
      // Update custom total price, status eyebrow, and lost-perk notices/CTA
      this.updateCustomTotal(total);
      this.updateCustomEyebrow(total);
      this.updateCustomNotices(total);
    }
    updateCustomNotices(total) {
      // One-time: no discount below 2 bags, no free shipping below 4 bags
      const showDiscountNotice = total >= 1 && total < 2;
      const showShippingNotice = total >= 1 && total < 4;
      
      let ctaText = '';
      if (total >= 1 && total < 2) {
        const needed = 2 - total;
        const discount2 = this.config.discounts[2] || 15;
        ctaText = `Add ${needed} more bag${needed !== 1 ? 's' : ''} to unlock ${discount2}% off!`;
      } else if (total >= 2 && total < 4) {
        const needed = 4 - total;
        ctaText = `Add ${needed} more bag${needed !== 1 ? 's' : ''} to unlock free shipping!`;
      }
      
      this.renderCustomNotices({
        noticesWrap: this.customNotices,
        shippingNotice: this.noticeShipping,
        discountNotice: this.noticeDiscount,
        ctaWrapper: this.customCtaWrapper,
        ctaEl: this.customCta
      }, showShippingNotice, showDiscountNotice, ctaText);
    }
    updateSubCustomNotices(total) {
      // Subscription: first discount (15%) at 2 bags, free shipping from 3 bags
      const showDiscountNotice = total >= 1 && total < 2;
      const showShippingNotice = total >= 1 && total < 3;
      
      let ctaText = '';
      if (total >= 1 && total < 2) {
        const subDiscount2 = this.config.subscriptionDiscounts[2] || 15;
        ctaText = `Add 1 more bag to unlock ${subDiscount2}% off!`;
      } else if (total >= 2 && total < 3) {
        const subDiscount3 = this.config.subscriptionDiscounts[3] || 40;
        ctaText = `Add 1 more bag to unlock ${subDiscount3}% off + free shipping!`;
      }
      
      this.renderCustomNotices({
        noticesWrap: this.subCustomNotices,
        shippingNotice: this.subNoticeShipping,
        discountNotice: this.subNoticeDiscount,
        ctaWrapper: this.subCustomCtaWrapper,
        ctaEl: this.subCustomCta
      }, showShippingNotice, showDiscountNotice, ctaText);
    }
    updateCustomEyebrow(total) {
      // One-time perks: discount from 2 bags, free shipping from 4 bags
      const discountPercent = this.getOnetimeDiscount(total);
      const showDiscount = total >= 1 && discountPercent > 0;
      const showShipping = total >= 4;
      this.renderEyebrow({
        wrap: this.customEyebrow,
        discountItem: this.eyebrowDiscount,
        discountText: this.eyebrowDiscountText,
        shippingItem: this.eyebrowShipping
      }, showDiscount, discountPercent, showShipping);
    }
    updateSubCustomEyebrow(total) {
      // Subscription perks: discount from 2 bags, free shipping from 3 bags
      const discountPercent = this.getSubDiscount(total);
      const showDiscount = total >= 1 && discountPercent > 0;
      const showShipping = total >= 3;
      this.renderEyebrow({
        wrap: this.subCustomEyebrow,
        discountItem: this.subEyebrowDiscount,
        discountText: this.subEyebrowDiscountText,
        shippingItem: this.subEyebrowShipping
      }, showDiscount, discountPercent, showShipping);
    }
    renderEyebrow(els, showDiscount, discountPercent, showShipping) {
      if (els.discountText && showDiscount) els.discountText.textContent = `${discountPercent}% OFF`;
      if (els.discountItem) els.discountItem.classList.toggle('is-visible', showDiscount);
      if (els.shippingItem) els.shippingItem.classList.toggle('is-visible', showShipping);
      if (els.wrap) {
        els.wrap.classList.toggle('is-visible', showDiscount || showShipping);
        els.wrap.classList.toggle('is-complete', showDiscount && showShipping);
      }
    }
    renderCustomNotices(els, showShipping, showDiscount, ctaText) {
      if (els.shippingNotice) els.shippingNotice.classList.toggle('is-visible', showShipping);
      if (els.discountNotice) els.discountNotice.classList.toggle('is-visible', showDiscount);
      if (els.noticesWrap) els.noticesWrap.classList.toggle('is-visible', showShipping || showDiscount);
      if (els.ctaEl) els.ctaEl.textContent = ctaText;
      if (els.ctaWrapper) els.ctaWrapper.classList.toggle('is-visible', ctaText !== '');
    }
    updateCustomTotal(total) {
      if (!this.customTotalOriginal || !this.customTotalDiscounted) return;
      
      const basePrice = this.config.variants[0]?.price || 0;
      
      // Find the applicable discount based on total quantity
      let discountPercent = 0;
      const discountKeys = Object.keys(this.config.discounts).map(Number).sort((a, b) => b - a);
      for (const qty of discountKeys) {
        if (total >= qty) {
          discountPercent = this.config.discounts[qty];
          break;
        }
      }
      
      const originalTotal = basePrice * total;
      const discountedTotal = this.getDiscountedUnitPrice(basePrice, discountPercent) * total;
      
      this.customTotalOriginal.textContent = total > 0 ? this.formatMoney(originalTotal) : '';
      this.customTotalDiscounted.textContent = total > 0 ? this.formatMoney(discountedTotal) : '';
      // Savings is taken from the two ROUNDED totals, not the raw floats, so
      // the three numbers on the line always agree when a shopper checks them.
      const savings = Math.round(originalTotal) - Math.round(discountedTotal);
      if (this.customTotalSavings) {
        this.customTotalSavings.textContent = savings > 0 ? `SAVE ${this.formatMoney(savings)}` : '';
      }
      // The struck price and the chip both hang off this class.
      if (this.customTotalRow) {
        this.customTotalRow.classList.toggle('has-savings', total > 0 && savings > 0);
      }
      if (this.customBags) {
        this.customBags.textContent = total > 0 ? `${total} bag${total !== 1 ? 's' : ''}` : '';
      }
    }
    // Subscription custom quantity methods
    initializeSubCustomQuantities() {
      const perFlavor = this.defaultCustomQtyPerFlavor();
      this.config.variants.forEach(v => {
        this.subCustomQuantities[v.id] = v.available ? perFlavor : 0;
      });
      this.updateSubCustomQuantityDisplays();
      this.updateSubProgressBar();
    }
    updateSubCustomQuantity(variantId, delta) {
      this.subCustomQuantities[variantId] = Math.max(0, (this.subCustomQuantities[variantId] || 0) + delta);
      // Keep one-time builder in sync so toggling modes never "loses" selections
      this.customQuantities[variantId] = this.subCustomQuantities[variantId];
      this.updateSubCustomQuantityDisplays();
      this.updateCustomQuantityDisplays();
      this.updateSubProgressBar();
      this.updateProgressBar();
    }
    updateSubCustomQuantityDisplays() {
      this.subCustomFlavors.forEach(flavorEl => {
        const variantId = parseInt(flavorEl.dataset.subVariantId);
        const qty = this.subCustomQuantities[variantId] || 0;
        const valueEl = flavorEl.querySelector('[data-sub-qty-value]');
        if (valueEl) valueEl.textContent = qty;
        flavorEl.classList.toggle('is-selected', qty > 0);
      });
    }
    getTotalSubCustomQuantity() {
      return Object.values(this.subCustomQuantities).reduce((sum, qty) => sum + qty, 0);
    }
    updateSubProgressBar() {
      const total = this.getTotalSubCustomQuantity();
      
      // Subscription tiers: 15% at 2 bags (no free shipping), then 40/50/55/60%
      // + free shipping at 3/4/6/8 bags
      const subDiscount2 = this.config.subscriptionDiscounts[2] || 15;
      const subDiscount3 = this.config.subscriptionDiscounts[3] || 40;
      const subDiscount4 = this.config.subscriptionDiscounts[4] || 50;
      const subDiscount6 = this.config.subscriptionDiscounts[6] || 55;
      const subDiscount8 = this.config.subscriptionDiscounts[8] || 60;
      
      let message = '';
      if (total === 0) {
        message = `Add 2 bags for <strong>${subDiscount2}% off!</strong>`;
      } else if (total === 1) {
        message = `Add 1 more bag for <strong>${subDiscount2}% off!</strong>`;
      } else if (total === 2) {
        message = `Add 1 more bag for <strong>${subDiscount3}% off + free shipping!</strong>`;
      } else if (total === 3) {
        message = `Add 1 more bag for <strong>${subDiscount4}% off!</strong>`;
      } else if (total === 4 || total === 5) {
        const needed = 6 - total;
        message = `Add ${needed} more bag${needed !== 1 ? 's' : ''} for <strong>${subDiscount6}% off!</strong>`;
      } else if (total >= 6 && total < 8) {
        const needed = 8 - total;
        message = `Add ${needed} more bag${needed !== 1 ? 's' : ''} for <strong>${subDiscount8}% off!</strong>`;
      } else if (total >= 8) {
        message = `<strong>${subDiscount8}% off + free shipping</strong> unlocked!`;
      }
      
      if (this.subProgressMessage) {
        this.subProgressMessage.innerHTML = message;
      }
      
      // Segments: 40% + free shipping at 3, 50% at 4, 55% at 6, 60% at 8
      this.applyProgressSegments(total, [3, 4, 6, 8], this.subProgressFills, this.subProgressSegments, this.subProgressCheckmarks);
      
      // Update custom total price, frequency, notices/CTA, and intro-offer disclosure
      this.updateSubCustomTotal(total);
      this.updateSubCustomEyebrow(total);
      this.updateSubCustomFrequency(total);
      this.updateSubCustomNotices(total);
      this.updateSubCustomDisclaimer(total);
    }
    updateSubCustomTotal(total) {
      if (!this.subCustomTotalOriginal || !this.subCustomTotalDiscounted) return;
      
      const basePrice = this.config.variants[0]?.price || 0;
      
      // Discounted price per bag (threshold-based; returns base price when no discount applies)
      const pricePerBag = this.getSubPriceForQuantity(total);
      
      const originalTotal = basePrice * total;
      const discountedTotal = pricePerBag * total;
      
      this.subCustomTotalOriginal.textContent = total > 0 ? this.formatMoney(originalTotal) : '';
      this.subCustomTotalDiscounted.textContent = total > 0 ? this.formatMoney(discountedTotal) : '';
      // Savings is taken from the two ROUNDED totals, not the raw floats, so
      // the three numbers on the line always agree when a shopper checks them.
      const savings = Math.round(originalTotal) - Math.round(discountedTotal);
      if (this.subCustomTotalSavings) {
        this.subCustomTotalSavings.textContent = savings > 0 ? `SAVE ${this.formatMoney(savings)}` : '';
      }
      // The struck price and the chip both hang off this class.
      if (this.subCustomTotalRow) {
        this.subCustomTotalRow.classList.toggle('has-savings', total > 0 && savings > 0);
      }
      if (this.subCustomBags) {
        this.subCustomBags.textContent = total > 0 ? `${total} bag${total !== 1 ? 's' : ''}` : '';
      }
    }
    updateSubCustomDisclaimer(total) {
      if (!this.subCustomDisclaimer) return;
      
      // Intro-offer disclosure only: the drop to -10% applies to the 3+ bag
      // tiers. The 2-bag 15% subscription keeps its rate on renewal, so the
      // disclosure is hidden entirely below 3 bags.
      const discountPercent = this.getSubDiscount(total);
      const show = total >= 3 && discountPercent > 0;
      
      if (show) {
        const renewalDiscount = discountPercent - 10;
        this.subCustomDisclaimer.textContent = `*Intro offer discount. Subscription will renew at ${renewalDiscount}% off.`;
      }
      this.subCustomDisclaimer.hidden = !show;
    }
    updateSubCustomFrequency(total) {
      if (!this.subCustomFrequency) return;
      
      if (total < 1) {
        this.subCustomFrequency.textContent = '';
        return;
      }
      
      // Frequency text: 1-3 → 1 month, 4-6 → 6 weeks, 7+ → 2 months
      let freqText;
      if (total >= 7) {
        freqText = '2 months';
      } else if (total >= 4) {
        freqText = '6 weeks';
      } else {
        freqText = 'month';
      }
      
      this.subCustomFrequency.textContent = `Shipped every ${freqText}—pause, change or cancel subscription anytime`;
    }
    async addTierToCart(tierQuantity, buttonElement) {
      const flavorCount = this.selectedFlavors.size;
      const selectedVariants = this.config.variants.filter(v => this.selectedFlavors.has(v.id));
      const distribution = this.getDistribution(flavorCount, tierQuantity);
      const items = selectedVariants.map((variant, index) => ({
        id: variant.id,
        quantity: distribution[index] || 0
      })).filter(item => item.quantity > 0);
      await this.updateCart(items, buttonElement, false);
    }
    async addCustomToCart() {
      // Build in on-screen order (config.variants == the order flavors are listed
      // in the builder) so the cart/checkout matches what the shopper sees.
      const items = this.config.variants
        .filter(v => (this.customQuantities[v.id] || 0) > 0)
        .map(v => ({ id: v.id, quantity: this.customQuantities[v.id] }));
      if (items.length === 0) return;
      await this.updateCart(items, this.customAddButton, false);
    }
    // Subscription cart functions - with selling_plan
    getSellingPlanIdForQuantity(quantity) {
      // Frequency mapping: 3 bags → 1 month, 4-6 → 6 weeks, 7+ → 2 months
      let planKey;
      if (quantity >= 7) {
        planKey = 3; // Every 2 months
      } else if (quantity >= 4) {
        planKey = 2; // Every 6 weeks
      } else {
        planKey = 1; // Every month
      }
      return this.config.sellingPlanIds[planKey];
    }
    async addSubTierToCart(tierQuantity, buttonElement) {
      const flavorCount = this.selectedFlavors.size;
      const selectedVariants = this.config.variants.filter(v => this.selectedFlavors.has(v.id));
      const distribution = this.getDistribution(flavorCount, tierQuantity);
      const sellingPlanId = this.getSellingPlanIdForQuantity(tierQuantity);
      
      const items = selectedVariants.map((variant, index) => ({
        id: variant.id,
        quantity: distribution[index] || 0,
        selling_plan: sellingPlanId
      })).filter(item => item.quantity > 0);
      
      await this.updateCart(items, buttonElement, true);
    }
    async addSubCustomToCart() {
      const total = this.getTotalSubCustomQuantity();
      if (total < 1) return;
      
      const sellingPlanId = this.getSellingPlanIdForQuantity(total);
      // On-screen order (config.variants), same as the one-time builder.
      const items = this.config.variants
        .filter(v => (this.subCustomQuantities[v.id] || 0) > 0)
        .map(v => ({
          id: v.id,
          quantity: this.subCustomQuantities[v.id],
          selling_plan: sellingPlanId
        }));
      if (items.length === 0) return;
      await this.updateCart(items, this.subCustomAddButton, true);
    }
    async updateCart(items, buttonElement, isSubscription = false) {
      // Prevent double-clicks
      if (this.isAddingToCart) return;
      this.isAddingToCart = true;
      
      if (buttonElement) buttonElement.classList.add('is-loading');
      
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        const productLineItems = cart.items.filter(item => item.product_id === this.config.productId);
        if (productLineItems.length > 0) {
          const updates = {};
          productLineItems.forEach(item => { updates[item.key] = 0; });
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
        }
        
        // Add bonus products if configured (only for non-subscription orders)
        if (!isSubscription && this.config.bonusProducts && this.config.bonusProducts.length > 0) {
          this.config.bonusProducts.forEach(bonus => {
            if (bonus.variantId) {
              items.push({
                id: bonus.variantId,
                quantity: bonus.quantity || 1
              });
            }
          });
        }
        
        // Shopify adds each new line at the TOP of the cart, so adding in list
        // order would leave cart.items (and therefore checkout) reversed. Add in
        // REVERSE list order so the final cart.items ends up in on-screen builder
        // order — flavors in display order, then any bonus products. Sequential
        // (one line per request) because a single multi-item /cart/add.js isn't
        // deterministic. Item count is small (one per flavor, plus bonuses).
        for (const item of items.slice().reverse()) {
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [item] })
          });

          if (!addResponse.ok) {
            const responseText = await addResponse.text();
            this.isAddingToCart = false;
            if (buttonElement) buttonElement.classList.remove('is-loading');
            alert('Error adding to cart: ' + responseText);
            return;
          }
        }

        window.location.href = '/checkout';
      } catch (error) {
        this.isAddingToCart = false;
        if (buttonElement) buttonElement.classList.remove('is-loading');
        alert('Error: ' + error.message);
      }
    }
    setupStickyObserver() {
      if (!this.stickyButton || !('IntersectionObserver' in window)) return;

      let bundlerInView = false;
      let visibleCtas = new Set();

      const updateVisibility = () => {
        this.stickyButton.classList.toggle('is-visible', !bundlerInView && visibleCtas.size === 0);
      };

      const bundlerObserver = new IntersectionObserver((entries) => {
        bundlerInView = entries[0].isIntersecting;
        updateVisibility();
      }, { threshold: 0.1 });
      bundlerObserver.observe(this.container);

      const externalCtas = document.querySelectorAll('[data-bundler-cta], .cpp__btn-wrap');
      if (externalCtas.length > 0) {
        const ctaObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            entry.isIntersecting ? visibleCtas.add(entry.target) : visibleCtas.delete(entry.target);
          });
          updateVisibility();
        }, { threshold: 0.1 });
        externalCtas.forEach(cta => ctaObserver.observe(cta));
      }

      // Pin the sticky bar to the bottom of the VISUAL viewport, not the layout
      // viewport. On iOS the layout viewport is fixed at the "toolbars-hidden"
      // height, so bottom:0 goes stale while Chrome's bottom tray animates in/out,
      // leaving the bar floating above the true bottom. Re-syncing on every
      // viewport change eliminates that gap.
      const vv = window.visualViewport;
      if (vv) {
        const positionSticky = () => {
          const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          this.stickyButton.style.bottom = overlap + 'px';
        };
        let rafId = null;
        const onViewportChange = () => {
          if (rafId) cancelAnimationFrame(rafId);
          rafId = requestAnimationFrame(positionSticky);
        };
        vv.addEventListener('resize', onViewportChange);
        vv.addEventListener('scroll', onViewportChange);
        positionSticky();
      }
    }
    isElementInViewport(el) {
      const rect = el.getBoundingClientRect();
      return rect.top >= 0 && rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth);
    }
    setupFocusObserver() {
      if (!this.focusOverlay || !('IntersectionObserver' in window)) return;
      
      let hasBeenActivated = false;
      let focusTimeout = null;
      
      const deactivateFocusMode = () => {
        this.focusOverlay.classList.remove('is-active');
        setTimeout(() => this.container.classList.remove('is-focus-active'), 400);
        if (focusTimeout) { clearTimeout(focusTimeout); focusTimeout = null; }
      };
      
      const activateFocusMode = () => {
        if (hasBeenActivated) return;
        hasBeenActivated = true;
        this.focusOverlay.classList.add('is-active');
        this.container.classList.add('is-focus-active');
        focusTimeout = setTimeout(deactivateFocusMode, 1500);
      };
      
      // Single unified observer for focus mode - handles both entry and exit
      const focusObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const rect = entry.boundingClientRect;
          if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
            activateFocusMode();
          } else if (hasBeenActivated && (entry.intersectionRatio < 0.4 && rect.top < 0 || rect.top > window.innerHeight)) {
            deactivateFocusMode();
          }
        });
      }, { threshold: [0, 0.1, 0.2, 0.4], rootMargin: '-50px 0px -30% 0px' });
      focusObserver.observe(this.container);
      
      // Observe next element to deactivate focus when scrolling past
      setTimeout(() => {
        let nextEl = this.container.nextElementSibling;
        while (nextEl && (nextEl.classList.contains('bundler__sticky') || nextEl.tagName === 'SCRIPT')) {
          nextEl = nextEl.nextElementSibling;
        }
        if (nextEl) {
          const belowObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.05) deactivateFocusMode();
            });
          }, { threshold: [0.05, 0.1] });
          belowObserver.observe(nextEl);
        }
      }, 100);
    }
    updateStickyText() {
      if (this.stickyText) {
        this.stickyText.textContent = this.currentStep === 1 ? this.config.stickyStep1Text : this.config.stickyStep2Text;
      }
    }
    scrollToSection() {
      this.container.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }
  function initProductBundlers() {
    document.querySelectorAll('.product-bundler[data-section-id]').forEach(container => {
      if (!container.dataset.initialized) {
        new ProductBundler(container);
        container.dataset.initialized = 'true';
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initProductBundlers);
  else initProductBundlers();
  document.addEventListener('shopify:section:load', (e) => {
    const container = e.target.querySelector('.product-bundler[data-section-id]');
    if (container) {
      container.dataset.initialized = '';
      new ProductBundler(container);
      container.dataset.initialized = 'true';
    }
  });
})();